import { forwardRef } from "react";
import { Button, ButtonProps } from "antd";
import { Loader2 } from "lucide-react";
import { useAppTheme } from "@/shared/hooks/useTheme";
import "./CustomButton.scss";

interface CustomButtonProps extends Omit<ButtonProps, "variant"> {
  /** Preferred: standard Cypress hook. Forwarded verbatim to the DOM. */
  "data-cy"?: string;
  /** @deprecated legacy alias — pass `data-cy` instead. */
  dataCy?: string;
  variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "danger"
    | "cancel"
    | "text"
    | "icon-only"
    | "default"
    | "warning"
    | "info";
}

const CustomButton = forwardRef<HTMLButtonElement, CustomButtonProps>(
  (
    {
      "data-cy": dataCyLiteral,
      dataCy,
      className = "",
      variant,
      type,
      danger,
      loading,
      icon,
      disabled,
      ...props
    },
    ref,
  ) => {
    const { theme } = useAppTheme();

    // Prefer the standard literal `data-cy`; fall back to the legacy `dataCy`
    // alias. No generic default — an unnamed button emits NO data-cy so it
    // can't collide with every other unnamed button on a shared value.
    const resolvedDataCy = dataCyLiteral ?? dataCy;

    // Map variant to Ant Design type and danger prop
    const buttonType: ButtonProps["type"] = variant
      ? variant === "primary"
        ? "primary"
        : variant === "outline"
          ? "default"
          : variant === "text"
            ? "text"
            : variant === "danger"
              ? "primary"
              : variant === "cancel"
                ? "default"
                : "default"
      : type;

    const isDanger = variant === "danger" || danger;
    // Normalize the loading flag — AntD allows `loading` to be either a
    // boolean or `{ delay: number }`. We only care whether the button is
    // currently in its loading state.
    const isLoading =
      typeof loading === "boolean"
        ? loading
        : typeof loading === "object" && loading !== null
          ? true
          : false;

    // Construct class names based on theme and variant
    const buttonClassName = `
    custom-button
    ${theme === "dark" ? "custom-button-dark" : "custom-button-light"}
    ${variant ? `custom-button-${variant}` : ""}
    ${className}
  `.trim();

    // Replace AntD's default `loading` spinner with Lucide's Loader2 +
    // Tailwind animate-spin. AntD's built-in spinner uses a separate CSS
    // animation that can clash with the project's transition rules and
    // renders inconsistently against the rest of the app (which uses
    // animate-spin everywhere — NavigationLoader, image uploads, etc.).
    // Passing `loading={false}` here suppresses the AntD spinner; we
    // render our own via the `icon` slot and disable the button while
    // loading so it can't be re-clicked.
    const renderedIcon = isLoading ? (
      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
    ) : (
      icon
    );

    return (
      <Button
        {...props}
        ref={ref}
        type={buttonType}
        danger={isDanger}
        className={buttonClassName}
        data-cy={resolvedDataCy}
        loading={false}
        icon={renderedIcon}
        disabled={disabled || isLoading}
      />
    );
  },
);

CustomButton.displayName = "CustomButton";

export default CustomButton;
